import { createSelector } from "@reduxjs/toolkit";
import { PricingModeEnum } from "api/pricing";
import { RootState } from "app/store";
import { ContractFunnel } from "../../../types/interfaces";
import { selectPricingState } from "./baseSelectors";
import { selectFilteredContractTunnels } from "./filtersSelectors";

export const selectPricingMode = createSelector(
  [selectPricingState],
  (pricingState) => pricingState.mode
)

export const selectFormulasContext = createSelector(
  [selectPricingState],
  (pricingState) => pricingState.formulasContext
)

/**
 * Formulas and comparable products are not selected together: while the cart holds an item of
 * the other family, adding one of this family is refused (the cart is not emptied for the user).
 * Never true in classic mode, where only comparable products exist.
 */
export const selectIsCartLockedForFamily = (isFixedFormula: boolean) => (state: RootState) => {
  const { mode, shoppingCart } = state.pricing;
  if (mode === PricingModeEnum.Classic || !shoppingCart) {
    return false;
  }
  return shoppingCart.some(item => !!item.product?.isFixedFormula !== isFixedFormula);
};

const isFixedFormula = (funnel: ContractFunnel) => !!funnel.product?.isFixedFormula;

/** Filtered cards of the comparable products (the classic results). */
export const selectFilteredComparableFunnels = createSelector(
  [selectFilteredContractTunnels],
  (funnels) => funnels.filter(funnel => !isFixedFormula(funnel))
)

/** Filtered cards of the fixed formulas. */
export const selectFilteredFormulaFunnels = createSelector(
  [selectFilteredContractTunnels],
  (funnels) => funnels.filter(isFixedFormula)
)

/** Labels of the formula products, in card order, each product once (for the sentence above the cards). */
export const selectFormulaProductLabels = createSelector(
  [selectPricingState],
  (pricingState) => {
    const seen = new Set<string>();
    return pricingState.contractFunnels
      .filter(funnel => isFixedFormula(funnel) && !seen.has(funnel.product.id) && seen.add(funnel.product.id))
      .map(funnel => funnel.product.label);
  }
)
